import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { apiClient } from '../../../services/apiClient';
import { brandConfig } from '../../../config/brands';
import { useToast } from '../../../hooks/useToast';
import { hardNavigate } from '../../../utils/navigation';
import { AuthErrorBanner } from '../../shared/AuthErrorBanner';
import { extractAsin } from '../../../features/products/asinInput';
import type { AmazonProductInfo } from '../../generation/express/useExpressMode';
// AsinSearchPage.tsx (frontend/src/pages/products/) のフォーム・アカウント選択スタイルを流用する。
import '../../../pages/products/ProductSearchPage.css';
import styles from './AsinSearchModal.module.css';

const SELECTED_ACCOUNT_KEY = 'selectedAmazonAccountId';

interface AmazonAccount {
  id: string;
  marketplace_id: string;
  seller_id: string;
  status: string;
}

interface AuthErrorInfo {
  error?: string;
  message?: string;
  action?: 'reauthenticate' | 'contact_admin';
  redirect_url?: string;
}

interface AsinSearchModalProps {
  onClose: () => void;
  /** 取得(=登録)成功時。GET /amazon/products/{asin} レスポンスの保存済み product をそのまま返す。 */
  onRegistered: (product: AmazonProductInfo) => void;
}

/**
 * ASIN検索モーダル (計画書§4.1)。AsinSearchPage.tsx のロジックをモーダル化したもの。
 * 実体は `GET /amazon/products/{asin}?account_id=` の呼び出し1回のみで、成功時に
 * 商品詳細ページへ navigate する代わりに onRegistered で保存済み product を返す。
 */
export function AsinSearchModal({ onClose, onRegistered }: AsinSearchModalProps) {
  const toast = useToast();
  const [asin, setAsin] = useState('');
  const [selectedAccountId, setSelectedAccountId] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [authError, setAuthError] = useState<AuthErrorInfo | null>(null);

  const { data: accountsData } = useQuery({
    queryKey: ['amazon-accounts'],
    queryFn: async () => {
      const response = await apiClient.get('/amazon/accounts');
      return response.data;
    },
  });

  const accounts: AmazonAccount[] = accountsData?.accounts || [];
  const selectedAccount = accounts.find((acc) => acc.id === selectedAccountId);
  const isAccountExpired = selectedAccount?.status === 'expired';

  // 初回ロード時にlocalStorageから前回選択したアカウントを復元
  useEffect(() => {
    if (accounts.length > 0 && !selectedAccountId) {
      const savedAccountId = localStorage.getItem(SELECTED_ACCOUNT_KEY);
      if (savedAccountId && accounts.find((acc) => acc.id === savedAccountId)) {
        setSelectedAccountId(savedAccountId);
      } else {
        setSelectedAccountId(accounts[0].id);
        localStorage.setItem(SELECTED_ACCOUNT_KEY, accounts[0].id);
      }
    }
  }, [accounts, selectedAccountId]);

  // アカウントが期限切れの場合、authErrorをセット
  useEffect(() => {
    if (isAccountExpired) {
      setAuthError({
        error: 'Amazon認証の有効期限が切れています',
        message: 'Amazon Seller Centralとの接続を再認証する必要があります。',
        action: 'reauthenticate',
        redirect_url: '/connections/amazon',
      });
    } else {
      setAuthError(null);
    }
  }, [isAccountExpired]);

  const handleAccountChange = (accountId: string) => {
    setSelectedAccountId(accountId);
    localStorage.setItem(SELECTED_ACCOUNT_KEY, accountId);
  };

  const handleFetchProduct = async () => {
    if (!asin.trim()) {
      setError('ASINを入力してください');
      return;
    }
    if (!selectedAccountId) {
      setError('Amazon Seller Accountを選択してください');
      return;
    }
    if (isAccountExpired) return;

    setIsLoading(true);
    setError(null);
    setAuthError(null);

    try {
      const extracted = extractAsin(asin);
      let resolvedAsin = extracted.asin;

      // 短縮URLの場合はサーバーサイドでリダイレクト解決
      if (extracted.needsResolve && extracted.originalUrl) {
        const { data: resolveData } = await apiClient.get(`/amazon/resolve-url?url=${encodeURIComponent(extracted.originalUrl)}`);
        resolvedAsin = resolveData.asin;
      }

      if (!resolvedAsin) {
        setError('ASINを抽出できませんでした');
        setIsLoading(false);
        return;
      }

      // 入力欄の表示値を抽出後のASINに置き換える（特急モードと同じ挙動）
      setAsin(resolvedAsin);

      const { data } = await apiClient.get(`/amazon/products/${resolvedAsin}?account_id=${selectedAccountId}`);
      const product = data.product as AmazonProductInfo;
      toast.success(`${brandConfig.labels.productShort}を登録しました`);
      onRegistered(product);
    } catch (err: any) {
      // 認証エラーの場合
      if (err.response?.status === 401 && err.response?.data?.action) {
        setAuthError(err.response.data);
        setError(null);
      } else {
        setError(err.response?.data?.error || '商品情報の取得に失敗しました');
      }
      setIsLoading(false);
    }
  };

  return (
    <div className="modal-overlay modal-overlay--nested" onClick={onClose}>
      <div className="modal-card modal-card--md" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <span className="modal-title">ASINから商品を取得</span>
          <button type="button" className="modal-close-button" aria-label="閉じる" onClick={onClose}>
            ✕
          </button>
        </div>
        <div className="modal-body">
          {accounts.length === 0 ? (
            <div className="account-register-form">
              <h3>Amazon Seller Account連携</h3>
              <p className="form-description">
                Amazon Sellerアカウントと連携して、商品情報を取得できます
              </p>
              <div className="form-info-box">
                <p>
                  安全なOAuth認証を使用してAmazonアカウントと連携します。
                  アプリケーションに必要な権限のみを付与できます。
                </p>
              </div>
              <div className="form-actions">
                <button
                  type="button"
                  className="btn-modal"
                  onClick={() => hardNavigate('/amazon/auth')}
                >
                  Amazonアカウントと連携
                </button>
              </div>
            </div>
          ) : (
            <>
              <div className="account-selector" style={{ marginBottom: 16 }}>
                <label htmlFor="asin-modal-account-select">Amazon Seller Account</label>
                <select
                  id="asin-modal-account-select"
                  value={selectedAccountId}
                  onChange={(e) => handleAccountChange(e.target.value)}
                  className="form-select"
                >
                  {accounts.map((account) => (
                    <option key={account.id} value={account.id}>
                      {account.seller_id} ({account.marketplace_id})
                    </option>
                  ))}
                </select>
              </div>

              {authError && (
                <div className={styles.authErrorWrap}>
                  <AuthErrorBanner error={authError} />
                </div>
              )}
              {error && !authError && (
                <div className="error-message" style={{ marginBottom: 16 }}>{error}</div>
              )}

              <div className="form-group-inline">
                <label htmlFor="asin-modal-input">ASIN</label>
                <input
                  id="asin-modal-input"
                  type="text"
                  value={asin}
                  onChange={(e) => setAsin(e.target.value)}
                  placeholder="例: https://www.amazon.co.jp/dp/B0XXXXXXXXX または ASIN"
                  className="form-input"
                  disabled={isLoading}
                  autoFocus
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      handleFetchProduct();
                    }
                  }}
                />
                <button
                  type="button"
                  className="btn-modal"
                  onClick={handleFetchProduct}
                  disabled={isLoading || !selectedAccountId || isAccountExpired || !asin.trim()}
                >
                  {isLoading ? '取得中...' : '検索して取り込む'}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
